import "./WaybillNoInput.less";
import React, { useCallback, useEffect, useState } from "react";
import { Input, message, Modal, Spin } from "antd";
import { useRequest, useUpdateEffect } from "ahooks";
import { shipValidate } from "@/utils/OrderUtils";
import orderApi, { updateLogisticsCompany } from "@/services/api/order";
import LogisticsCompanyNameMap from "@/map/LogisticsCompanyNameMap";
import LogisticsCompany from "@/enum/LogisticsCompany";
import { OrderType } from "@/scenes/OrderManagement/redux/states/TableState";
import ShopType from "@/enum/ShopType";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import { observer } from "mobx-react-lite";
import { getFirstValidTrackingUrl } from "@/services/trackingUrl";
import { useGlobalStore } from "@/store/hooks";
import { checkShopIdsEnabledAutoPrint } from "@/scenes/more/AutoPrint/services";
import OrderLogisticsCompanySelect from "./OrderLogisticsCompanySelect";
import { identifyExpressCompany } from "@/utils/LogisticsCompanyUtil";
import OrderDTO from "@/dto/order/OrderDTO";
import WaybillNoGenerator from "@/utils/WaybillNoGenerator";

interface WaybillNoInputProps {
    order: OrderType;

    shopType: ShopType;
}

interface TrackingButtonProps {
    logisticsCompany: LogisticsCompany;
    waybillNo?: string;
}

const TrackingButton: React.FC<TrackingButtonProps> = React.memo(function (props) {
    const { logisticsCompany, waybillNo } = props;

    const result = getFirstValidTrackingUrl(logisticsCompany, waybillNo);

    if (!result) {
        return null;
    }

    return (
        <span className="input-group-append">
            <a
                className="btn btn-default btn-xs tracking-btn"
                href={result.url}
                target="_blank"
                rel="noopener noreferrer"
                type="button"
            >
                查
            </a>
        </span>
    );
});

function hasEwaybillNo(order: OrderType) {
    return order.hasEwaybillNo && order.waybillNo && order.waybillNo.length;
}

function getSelectedOrders(orders: OrderDTO[], startIndex) {
    const selectedOrders = [];
    orders.forEach((order, index) => {
        if (index < startIndex) return;
        // @ts-ignore
        if (order.isSelected) {
            selectedOrders.push(order);
        }
    });
    return selectedOrders;
}

function checkIfHasDiffLogisticsCompany(selectOrders: OrderType[]) {
    const firstLogisticsCompany = selectOrders[0].logisticsCompany;
    return selectOrders.some((order) => order.logisticsCompany !== firstLogisticsCompany);
}
function isAnyOrderHasEwaybilNo(selectOrders: OrderType[]) {
    return selectOrders.some((order) => {
        return hasEwaybillNo(order);
    });
}

const WaybillNoInput: React.FC<WaybillNoInputProps> = function (props) {
    const globalStore = useGlobalStore();
    const orderManagementStore = useOrderManagementStore();

    const { order, shopType } = props;

    const [showTrackingButton, setShowTrackingButton] = useState(false);

    const [waybillNo, setWaybillNo] = useState(order.waybillNo);

    const logisticsCompany = order.logisticsCompany || order.designatedLogisticsCompany;

    const error = order.waybillNoInputValidateErrorMsg;

    const { runAsync, loading } = useRequest(
        async (newOrder, newWaybillNo: string, oldWaybillNo: string) => {
            const shop = globalStore.shopMapping.get(order.shopId);
            // 校验是否开启自动打印
            const next = await checkShopIdsEnabledAutoPrint([shop], true);

            if (!next) {
                orderManagementStore.table.updateOrder(newOrder.id, { waybillNo: oldWaybillNo });

                return;
            }

            await orderApi.updateWaybillNo(newOrder, newWaybillNo);

            message.destroy();

            message.success({ content: "运单号修改成功" });
        },
        {
            onError() {
                Modal.error({
                    title: "运单号修改失败, 请刷新页面后重试！",
                });
            },
            manual: true,
        },
    );

    // 该方法目的：订单运单号校验时出现‘没有物流公司’错误，当用户打印快递单后（为订单设置了物流公司后）重新校验并隐藏错误信息
    useEffect(() => {
        if (order.waybillNoInputValidationActived) {
            validate();
        }
    }, [logisticsCompany, order?.waybillNo]);

    useUpdateEffect(() => {
        setWaybillNo(order.waybillNo);
    }, [order.waybillNo]);

    const onInputChange = useCallback(
        (event) => {
            const newWaybillNo = event.target.value.trim();

            setWaybillNo(newWaybillNo);
        },
        [order],
    );

    const onInputFocus = useCallback(() => {
        setShowTrackingButton(false);
    }, []);

    /**
     * 运单号输入框失去聚焦
     */
    async function onInputBlur(event) {
        setShowTrackingButton(true);

        if (order.waybillNoInputValidationActived) {
            validate();
        }

        const newWaybillNo = event.target.value.trim();

        setWaybillNo(newWaybillNo);

        if (newWaybillNo !== order.waybillNo) {
            // 如果都是空则跳过
            if (!newWaybillNo && !order.waybillNo) {
                return;
            }

            const oldWaybillNo = order.waybillNo;

            orderManagementStore.table.updateOrder(order.id, { waybillNo: newWaybillNo });

            await runAsync(order, newWaybillNo, oldWaybillNo);

            const logisticsCompany = identifyExpressCompany(newWaybillNo);

            if (logisticsCompany) {
                message.success(`已自动识别快递公司【${LogisticsCompanyNameMap[logisticsCompany]}】`);

                updateLogisticsCompany([order], logisticsCompany);

                orderManagementStore.table.updateOrder(order.id, {
                    logisticsCompany,
                });
            }
        }
    }
    /**
     * 校验（发货前校验），如果不符合规则显示错误
     * 规则：
     * 1. 物流公司id不了为空
     * 2. 运单号不了为空
     * 3. 运单号必须符合物流公司要求
     */
    const validate = useCallback(() => {
        const validateError = shipValidate(shopType, order.waybillNo, logisticsCompany);

        orderManagementStore.table.updateOrder(order.id, {
            waybillNoInputValidationActived: true,
            waybillNoInputValidateErrorMsg: validateError,
        });
    }, [order, shopType]);

    function onButtonClick(e: React.MouseEvent) {
        e?.preventDefault();
        if (!waybillNo) return Modal.error({ title: "请先输入运单号" });
        onAutoInputWaybillNoButtonClick();
    }

    async function onAutoInputWaybillNoButtonClick() {
        const findIndex = orderManagementStore.table.orders.findIndex((it) => it.id === order.id);
        if (findIndex === -1) return;
        const startIndex = findIndex + 1;
        const selectedOrders = getSelectedOrders(orderManagementStore.table.orders, startIndex);
        if (selectedOrders.length === 0) {
            return Modal.error({ title: "请先选中你要联想录入运单号的订单" });
        }
        if (checkIfHasDiffLogisticsCompany(selectedOrders)) {
            return Modal.error({
                title: "选中的订单中包含了多个快递公司打印，无法联想运单号",
            });
        }
        if (isAnyOrderHasEwaybilNo(selectedOrders)) {
            const result = await confirm(
                "所勾选订单某些订单已经打印电子面单，自动联想录入单号将忽略这些已经打印了电子面单的订单！",
            );
            if (!result) return;
        }
        const logisticsCompany = selectedOrders[0].logisticsCompany;
        const waybillNoGenerator = new WaybillNoGenerator(logisticsCompany, waybillNo, 1, 1);
        for (let i = 0; i < selectedOrders.length; i++) {
            const selectOrder = selectedOrders[i];
            if (hasEwaybillNo(selectOrder)) return;
            const nextWaybillNo = waybillNoGenerator.next();
            const oldWaybillNo = selectOrder.waybillNo;
            orderManagementStore.table.updateOrder(selectOrder.id, { waybillNo: nextWaybillNo, hasEwaybillNo: false });
            await runAsync(selectOrder, nextWaybillNo, oldWaybillNo);
        }
    }

    return (
        <Spin spinning={loading}>
            {error ? (
                <span className="error text-danger">
                    {error === "没有物流公司" ? (
                        <span>
                            未选择快递公司(
                            <a href="https://qw15p3s8hv.feishu.cn/wiki/wikcnzaAJZzPsILdFAi11zH27gg" target="_blank">
                                教程
                            </a>
                            )
                        </span>
                    ) : (
                        error
                    )}
                </span>
            ) : null}

            <div className="waybill-no">
                <div>
                    <OrderLogisticsCompanySelect order={order} />
                </div>

                <div className="input-group">
                    <Input
                        size="small"
                        value={waybillNo || ""}
                        onChange={onInputChange}
                        onFocus={onInputFocus}
                        onBlur={onInputBlur}
                    />
                    {showTrackingButton ? (
                        <TrackingButton logisticsCompany={logisticsCompany} waybillNo={waybillNo}></TrackingButton>
                    ) : null}
                </div>

                <div>
                    <a href="" className="auto-input-waybill-no-button" onClick={onButtonClick}>
                        联想输入运单号↓
                    </a>
                </div>
            </div>
        </Spin>
    );
};

export default observer(WaybillNoInput);
